import React, { useState, useEffect } from 'react';
import { Search, UserPlus, Building2, Users, Calendar, CheckCircle, XCircle, ShieldAlert, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';

// Frontend gate for the admin console. Defense-in-depth only — the edge functions
// it calls must enforce admin auth server-side too (see go-live task #61).
const ADMIN_EMAILS = ['nitintangellamudi@gmail.com'];

interface EnterpriseAccount {
  user_id: string;
  user_email: string;
  seat_count: number;
  billing_cycle: string;
  current_period_start: string;
  current_period_end: string;
  status: string;
  created_at: string;
}

export const AdminEnterprisePage: React.FC = () => {
  const [enterpriseAccounts, setEnterpriseAccounts] = useState<EnterpriseAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [seatCount, setSeatCount] = useState<number>(10);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [access, setAccess] = useState<'checking' | 'ok' | 'denied'>('checking');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const email = data.user?.email ?? null;
      setAccess(email && ADMIN_EMAILS.includes(email) ? 'ok' : 'denied');
    });
  }, []);

  useEffect(() => {
    if (access === 'ok') loadEnterpriseAccounts();
  }, [access]);

  const loadEnterpriseAccounts = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-enterprise-accounts`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session?.access_token ?? ''}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to load enterprise accounts');
      }

      const data = await response.json();
      setEnterpriseAccounts(data || []);
    } catch (err) {
      console.error('Exception loading enterprise accounts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load enterprise accounts. Make sure you have admin access.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!upgradeEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (seatCount < 10) {
      setError('Enterprise tier requires minimum 10 seats');
      return;
    }

    try {
      setUpgrading(true);
      setError(null);
      setSuccess(null);

      // Call upgrade function via edge function
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-upgrade-enterprise`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session?.access_token ?? ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: upgradeEmail.trim(),
          seatCount,
          billingCycle
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upgrade user');
      }

      const result = await response.json();
      setSuccess(result.message || `Successfully upgraded ${upgradeEmail} to Enterprise tier with ${seatCount} seats`);
      setUpgradeEmail('');
      setSeatCount(10);
      loadEnterpriseAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upgrade user');
    } finally {
      setUpgrading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredAccounts = enterpriseAccounts.filter(account =>
    account.user_email.toLowerCase().includes(searchEmail.toLowerCase())
  );

  if (access === 'checking') {
    return (
      <div className="flex items-center justify-center py-32 text-gray-500">
        <Loader className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (access === 'denied') {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-black mb-2">Not Authorized</h1>
        <p className="text-gray-600">This console is restricted to administrators.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-black mb-2">Enterprise Account Management</h1>
        <p className="text-gray-600">Manage Enterprise tier subscriptions and seat counts</p>
      </div>

      {/* Upgrade User Section */}
      <div className="backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-xl p-6 shadow-sm mb-6">
        <h2 className="text-xl font-semibold text-black mb-4 flex items-center space-x-2">
          <UserPlus className="w-5 h-5" />
          <span>Upgrade User to Enterprise</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-AdminEnterprisePagetsx-0">
              User Email
            </label>
            <input
              type="email"
              value={upgradeEmail}
              id="fld-AdminEnterprisePagetsx-0" onChange={(e) => setUpgradeEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-AdminEnterprisePagetsx-1">
              Seat Count
            </label>
            <input
              type="number"
              min="10"
              value={seatCount}
              id="fld-AdminEnterprisePagetsx-1" onChange={(e) => setSeatCount(parseInt(e.target.value) || 10)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-AdminEnterprisePagetsx-2">
              Billing Cycle
            </label>
            <select
              value={billingCycle}
              id="fld-AdminEnterprisePagetsx-2" onChange={(e) => setBillingCycle(e.target.value as 'monthly' | 'annual')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="w-full px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {upgrading ? 'Upgrading...' : 'Upgrade to Enterprise'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}
      </div>

      {/* Enterprise Accounts List */}
      <div className="backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-black flex items-center space-x-2">
            <Building2 className="w-5 h-5" />
            <span>Enterprise Accounts ({enterpriseAccounts.length})</span>
          </h2>
          <div className="flex items-center space-x-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading enterprise accounts...</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            {searchEmail ? 'No accounts found matching your search' : 'No enterprise accounts yet'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Seats</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Billing</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Period End</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={account.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">{account.user_email}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>{account.seat_count}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700 capitalize">
                      {account.billing_cycle || 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{formatDate(account.current_period_end)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {account.status === 'active' ? (
                        <span className="flex items-center space-x-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>Active</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 text-red-600">
                          <XCircle className="w-4 h-4" />
                          <span className="capitalize">{account.status}</span>
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatDate(account.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

